import {
  normalizeViewConfig,
  readViewConfig as readViewConfigCore,
  type DatabaseViewConfig,
} from '@couplespace/core/databases';

import { fetchBlock, updateBlock } from '@/lib/blocks/api';
import { newBlockId } from '@/lib/blocks/ids';
import type { BlockRecord } from '@/lib/blocks/types';

export type { DatabaseView, DatabaseViewConfig, DatabaseViewType } from '@couplespace/core/databases';

export function createViewId(): string {
  return newBlockId();
}

export function readViewConfigFromBlock(block: BlockRecord): DatabaseViewConfig {
  return readViewConfigCore(block.content, createViewId);
}

export function serializeViewConfig(config: DatabaseViewConfig): string {
  return JSON.stringify(config);
}

export function parseViewConfigJson(
  json: string,
  seed?: DatabaseViewConfig,
): DatabaseViewConfig {
  const trimmed = json.trim();
  if (!trimmed) {
    return seed ?? normalizeViewConfig(null, createViewId);
  }
  try {
    return normalizeViewConfig(JSON.parse(trimmed) as unknown, createViewId);
  } catch {
    return seed ?? normalizeViewConfig(null, createViewId);
  }
}

export type ViewStore = {
  read: () => DatabaseViewConfig;
  save: (next: DatabaseViewConfig) => Promise<void>;
};

export async function saveViewConfig(
  block: BlockRecord,
  viewConfig: DatabaseViewConfig,
): Promise<BlockRecord> {
  return updateBlock(block.id, {
    content: { ...block.content, viewConfig },
    expectedSyncVersion: block.syncVersion,
  });
}

export function isViewConfigConflict(err: unknown): boolean {
  if (!(err instanceof Error)) {
    return false;
  }
  return (
    err.message.includes('CONFLICT') || err.message.includes('modified by another session')
  );
}

export async function saveViewConfigWithRetry(
  databaseId: string,
  viewConfig: DatabaseViewConfig,
  fetchBlockFn: (id: string) => Promise<BlockRecord> = fetchBlock,
): Promise<BlockRecord> {
  let block = await fetchBlockFn(databaseId);
  try {
    return await saveViewConfig(block, viewConfig);
  } catch (err) {
    if (!isViewConfigConflict(err)) {
      throw err;
    }
    block = await fetchBlockFn(databaseId);
    return saveViewConfig(block, viewConfig);
  }
}

export function createBlockViewStore(
  blockId: string,
  options?: {
    fetchBlockFn?: (id: string) => Promise<BlockRecord>;
    onBlockUpdated?: (block: BlockRecord) => void;
    getCachedBlock?: () => BlockRecord | null;
  },
): ViewStore {
  const fetchBlockFn = options?.fetchBlockFn ?? fetchBlock;
  let cached = options?.getCachedBlock?.() ?? null;

  return {
    read() {
      if (cached) {
        return readViewConfigFromBlock(cached);
      }
      return normalizeViewConfig(null, createViewId);
    },
    async save(next) {
      const updated = await saveViewConfigWithRetry(blockId, next, fetchBlockFn);
      cached = updated;
      options?.onBlockUpdated?.(updated);
    },
  };
}

/** In-memory store; caller syncs read/save with React state or embed props. */
export function createMemoryViewStore(
  getConfig: () => DatabaseViewConfig,
  saveConfig: (next: DatabaseViewConfig) => Promise<void>,
): ViewStore {
  return {
    read: getConfig,
    save: saveConfig,
  };
}
